import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Share2,
  ChevronLeft,
  ChevronRight,
  Download,
  FolderDown,
  MessageCircle,
  Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ShareCarouselDialog = ({ message }) => {
  const [open, setOpen] = useState(false);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!open || slides.length > 0) return;
    setLoading(true);
    api
      .get(`/messages/${message.id}/social-carousel`)
      .then((res) => setSlides(res.data.slides || []))
      .catch(() => toast.error("No se pudo preparar el carrusel. Intenta de nuevo."))
      .finally(() => setLoading(false));
  }, [open, message.id, slides.length]);

  const slideUrl = (slide) => `${BACKEND_URL}${slide.url}`;

  const downloadSlide = useCallback(async () => {
    const slide = slides[current];
    if (!slide) return;
    setDownloading(true);
    try {
      const res = await fetch(slideUrl(slide));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${String(current + 1).padStart(2, "0")}-${slide.key}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Imagen descargada");
    } catch {
      toast.error("No se pudo descargar la imagen");
    } finally {
      setDownloading(false);
    }
  }, [slides, current]);

  const downloadZip = () => {
    window.location.href = `${BACKEND_URL}/api/messages/${message.id}/social.zip`;
    toast.success("Descargando el carrusel completo…");
  };

  const shareText = `${message.titulo} — ${message.pasaje}\n\nLee el mensaje completo en Centro de Fortaleza Espiritual:\n${window.location.origin}/mensaje/${message.id}`;

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank", "noopener");
  };

  const shareNative = async () => {
    setSharing(true);
    try {
      // Intentar compartir las imagenes como archivos (movil)
      if (navigator.canShare) {
        const files = [];
        for (const slide of slides) {
          const res = await fetch(slideUrl(slide));
          const blob = await res.blob();
          files.push(new File([blob], `${slide.key}.png`, { type: "image/png" }));
        }
        if (navigator.canShare({ files })) {
          await navigator.share({ files, title: message.titulo, text: shareText });
          return;
        }
      }
      if (navigator.share) {
        await navigator.share({ title: message.titulo, text: shareText });
        return;
      }
      await navigator.clipboard.writeText(shareText);
      toast.success("Texto copiado. Pégalo en tu red social junto a las imágenes descargadas.");
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.info("Descarga las imágenes y compártelas desde tu galería.");
      }
    } finally {
      setSharing(false);
    }
  };

  const slide = slides[current];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="bg-card" data-testid="share-carousel-button">
          <Share2 className="mr-2 h-4 w-4" /> Compartir en redes
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[92vh] overflow-y-auto bg-card sm:max-w-xl" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Carrusel para redes sociales</DialogTitle>
          <DialogDescription>
            Imágenes listas (1080×1080) para compartir en WhatsApp, Instagram o Facebook.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <Skeleton className="aspect-square w-full rounded-[var(--radius)]" />
        ) : slides.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No se pudo cargar el carrusel. Cierra y vuelve a intentar.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="relative overflow-hidden rounded-[var(--radius)] border border-border bg-muted">
              <img
                key={slide.key}
                src={slideUrl(slide)}
                alt={slide.title}
                className="aspect-square w-full object-contain"
                data-testid="share-slide-image"
              />
              {slides.length > 1 && (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-card/90"
                    onClick={() => setCurrent((c) => (c - 1 + slides.length) % slides.length)}
                    data-testid="share-prev"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-card/90"
                    onClick={() => setCurrent((c) => (c + 1) % slides.length)}
                    data-testid="share-next"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </>
              )}
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-foreground" data-testid="share-slide-title">
                {slide.title}
              </p>
              <div className="flex items-center gap-1.5" data-testid="share-dots">
                {slides.map((s, i) => (
                  <button
                    key={s.key}
                    onClick={() => setCurrent(i)}
                    className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ${
                      i === current ? "w-6 bg-primary" : "w-2.5 bg-border hover:bg-accent"
                    }`}
                    aria-label={`Ir al slide ${i + 1}`}
                  />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" onClick={downloadSlide} disabled={downloading} data-testid="share-download-slide">
                {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                Esta imagen
              </Button>
              <Button variant="outline" onClick={downloadZip} data-testid="share-download-zip">
                <FolderDown className="mr-2 h-4 w-4" /> Todas (ZIP)
              </Button>
              <Button
                onClick={shareWhatsApp}
                className="bg-[#25D366] text-white hover:bg-[#1ebe5b]"
                data-testid="share-whatsapp"
              >
                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
              </Button>
              <Button onClick={shareNative} disabled={sharing} data-testid="share-native">
                {sharing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Share2 className="mr-2 h-4 w-4" />}
                Compartir
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              Consejo: en Instagram descarga todas las imágenes y súbelas como carrusel. En WhatsApp puedes
              compartir el enlace o las imágenes desde tu galería.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
